/**
 * Sprint 0147 — Weighbridge Correction Workflow API client.
 *
 * Backend: app/api/routers/weighbridge.py (under /weighbridge/corrections).
 *
 * Roles:
 *   raise   → GATE_ENTRY_OPERATOR / SECURITY_SUPERVISOR / SECURITY_GUARD
 *   approve → ACCOUNTANT / ACCOUNTS_MANAGER / CFO
 *   notify  → CFO + CEO + CHAIRMAN (the "MD + CFO" tier)
 */
import apiClient from '@/shared/api/client'

export const CORRECTION_TYPES = [
  'GROSS', 'TARE', 'BOTH', 'RECON_FIELDS', 'MULTIPLE',
] as const
export type CorrectionType = (typeof CORRECTION_TYPES)[number]

export const CORRECTION_STATUSES = [
  'PENDING', 'APPROVED', 'REJECTED', 'SUPERSEDED',
] as const
export type CorrectionStatus = (typeof CORRECTION_STATUSES)[number]

export const STATUS_COLOR: Record<CorrectionStatus, string> = {
  PENDING: 'processing',
  APPROVED: 'success',
  REJECTED: 'error',
  SUPERSEDED: 'default',
}

export interface WeighbridgeCorrection {
  id: string
  correction_no: string
  gate_entry_id: string
  weighbridge_log_id: string | null
  supersedes_correction_id: string | null
  correction_type: CorrectionType
  status: CorrectionStatus

  // Originals (snapshot at raise time)
  original_gross_kg: number | null
  original_tare_kg: number | null
  original_net_kg: number | null
  original_moisture_pct: number | null
  original_rate_per_kg: number | null
  original_freight_charge: number | null
  original_other_charges: number | null

  // Corrected
  corrected_gross_kg: number | null
  corrected_tare_kg: number | null
  corrected_net_kg: number | null
  corrected_moisture_pct: number | null
  corrected_rate_per_kg: number | null
  corrected_freight_charge: number | null
  corrected_other_charges: number | null

  reason: string
  raised_by: string
  raised_at: string
  approved_by: string | null
  approved_at: string | null
  approver_notes: string | null
  reject_reason: string | null

  created_at: string
  updated_at: string

  // List-only enrichment
  inward_no?: string | null
}

export interface RaiseBody {
  gate_entry_id: string
  weighbridge_log_id?: string | null
  correction_type: CorrectionType
  reason: string
  corrected_gross_kg?: number | null
  corrected_tare_kg?: number | null
  corrected_moisture_pct?: number | null
  corrected_rate_per_kg?: number | null
  corrected_freight_charge?: number | null
  corrected_other_charges?: number | null
}

export const weighbridgeCorrectionsApi = {
  raise: async (body: RaiseBody) =>
    (await apiClient.post<WeighbridgeCorrection>(
      '/weighbridge/corrections', body,
    )).data,

  list: async (params: {
    gate_entry_id?: string
    status?: CorrectionStatus | 'ALL'
    limit?: number
  } = {}) =>
    (await apiClient.get<WeighbridgeCorrection[]>(
      '/weighbridge/corrections', { params },
    )).data,

  get: async (id: string) =>
    (await apiClient.get<WeighbridgeCorrection>(
      `/weighbridge/corrections/${id}`,
    )).data,

  approve: async (id: string, approver_notes?: string) =>
    (await apiClient.post<WeighbridgeCorrection>(
      `/weighbridge/corrections/${id}/approve`,
      { approver_notes },
    )).data,

  reject: async (id: string, reject_reason: string) =>
    (await apiClient.post<WeighbridgeCorrection>(
      `/weighbridge/corrections/${id}/reject`,
      { reject_reason },
    )).data,
}
